"use strict";
// Copyright © Veeam Software Group GmbH.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TokenManager = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
class TokenManager {
    constructor(tokenStorage, tokenCreator, tokenRefresher, tokenRevoker, shouldUseGlobalTokenRefreshing) {
        this.tokenStorage = tokenStorage;
        this.tokenCreator = tokenCreator;
        this.tokenRefresher = tokenRefresher;
        this.tokenRevoker = tokenRevoker;
        this.shouldUseGlobalTokenRefreshing = shouldUseGlobalTokenRefreshing;
        this.shareRefreshToken$ = null;
        this.tokenRefreshPoolingInterval = 1000;
        this.tokenRefreshPoolingTimeout = 15000;
    }
    createShareRefreshToken() {
        return (0, rxjs_1.of)(null).pipe((0, operators_1.concatMap)(() => (0, rxjs_1.from)(this.tokenRefresher(this.tokenStorage.getValue()))), (0, operators_1.catchError)((error) => (0, rxjs_1.from)(this.clearToken()).pipe((0, operators_1.concatMapTo)((0, rxjs_1.throwError)(() => error)))), (0, operators_1.tap)((value) => {
            this.setStorageValue(value);
        }), (0, operators_1.share)({ resetOnComplete: true, resetOnError: true, resetOnRefCountZero: false }));
    }
    setStorageValue(storageValue) {
        return this.tokenStorage.setValue(storageValue);
    }
    getTokenRefreshing() {
        var _a, _b;
        return (_b = (_a = this.tokenStorage.getValue()) === null || _a === void 0 ? void 0 : _a.tokenRefreshing) !== null && _b !== void 0 ? _b : false;
    }
    tokenRefreshingIsTimeout(tickLeft) {
        return tickLeft * this.tokenRefreshPoolingInterval < this.tokenRefreshPoolingTimeout;
    }
    checkIfTokenIsRefreshing() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.shouldUseGlobalTokenRefreshing && this.getTokenRefreshing()) {
                return yield (0, rxjs_1.firstValueFrom)((0, rxjs_1.interval)(this.tokenRefreshPoolingInterval).pipe((0, operators_1.skipWhile)((tickLeft) => this.getTokenRefreshing() && this.tokenRefreshingIsTimeout(tickLeft)), (0, operators_1.map)((tickLeft) => this.tokenRefreshingIsTimeout(tickLeft))));
            }
            return false;
        });
    }
    hasToken() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.tokenStorage.getValue() !== null;
        });
    }
    getAccessToken() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            return (_b = (_a = this.tokenStorage.getValue()) === null || _a === void 0 ? void 0 : _a.accessToken) !== null && _b !== void 0 ? _b : null;
        });
    }
    getAccessTokenSync() {
        var _a, _b;
        return (_b = (_a = this.tokenStorage.getValue()) === null || _a === void 0 ? void 0 : _a.accessToken) !== null && _b !== void 0 ? _b : null;
    }
    getRefreshToken() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            return (_b = (_a = this.tokenStorage.getValue()) === null || _a === void 0 ? void 0 : _a.refreshToken) !== null && _b !== void 0 ? _b : null;
        });
    }
    getStorageValue() {
        return this.tokenStorage.getValue();
    }
    setTokenRefreshing(value) {
        const storageValue = this.getStorageValue();
        if (storageValue === null) {
            return false;
        }
        const newStorageValue = Object.assign(Object.assign({}, storageValue), { tokenRefreshing: value });
        this.setStorageValue(newStorageValue);
        return true;
    }
    createToken(config) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const tokenStorageValue = yield this.tokenCreator(config);
                this.setStorageValue(tokenStorageValue);
            }
            catch (err) {
                this.tokenStorage.clear();
                throw err;
            }
            return undefined;
        });
    }
    refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const isTokenRefreshing = yield this.checkIfTokenIsRefreshing();
            if (isTokenRefreshing) {
                return;
            }
            this.setTokenRefreshing(true);
            try {
                if (!this.shareRefreshToken$) {
                    this.shareRefreshToken$ = this.createShareRefreshToken();
                }
                return yield (0, rxjs_1.firstValueFrom)(this.shareRefreshToken$);
            }
            catch (err) {
                throw err;
            }
            finally {
                this.setTokenRefreshing(false);
            }
        });
    }
    revokeToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.tokenRevoker(this.tokenStorage.getValue());
            }
            catch (err) {
                this.tokenStorage.clear();
                throw err;
            }
        });
    }
    clearToken() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.tokenStorage.clear();
        });
    }
    createRefreshTokenOperator() {
        return (source) => {
            return source.pipe((0, operators_1.zipWith)((0, rxjs_1.of)(1, 2)), (0, operators_1.mergeMap)(([error, count]) => {
                if (count === 1 && (error === null || error === void 0 ? void 0 : error.status) === 401) {
                    return (0, rxjs_1.from)(this.hasToken()).pipe((0, operators_1.mergeMap)((hasToken) => (hasToken ? (0, rxjs_1.from)(this.refreshToken()) : rxjs_1.EMPTY)), (0, operators_1.throwIfEmpty)(), (0, operators_1.catchError)((refreshError) => {
                        return (0, rxjs_1.throwError)(() => {
                            if (refreshError instanceof rxjs_1.EmptyError) {
                                return error;
                            }
                            refreshError.status = 401;
                            return refreshError;
                        });
                    }));
                }
                return (0, rxjs_1.throwError)(() => error);
            }));
        };
    }
}
exports.TokenManager = TokenManager;
//# sourceMappingURL=TokenManager.js.map